import React, { useState, useEffect, useMemo } from 'react';
import { getSuppliers, getSupplierCodes, createSupplierCode, updateSupplierCode, deleteSupplierCode } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Loader2, Barcode } from 'lucide-react';

const UNITS = ['kg', 'litres', 'each', 'box', 'pallet', 'other'];
const empty = { supplier_id: '', internal_item: '', supplier_product_code: '', supplier_product_name: '', unit: 'kg', notes: '' };

export default function SupplierProductCodes() {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [supFilter, setSupFilter] = useState('all');
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([getSupplierCodes(), getSuppliers()]);
      setItems(c.data); setSuppliers(s.data);
    } catch (e) { toast.error('Failed to load mappings'); }
    finally { setLoading(false); }
  })(); }, []);

  const reload = async () => { const c = await getSupplierCodes(); setItems(c.data); };
  const supName = (id) => suppliers.find(s => s.id === id)?.name || '—';

  const filtered = useMemo(() => items.filter(it => {
    if (supFilter !== 'all' && it.supplier_id !== supFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (it.internal_item || '').toLowerCase().includes(q)
      || (it.supplier_product_code || '').toLowerCase().includes(q)
      || (it.supplier_product_name || '').toLowerCase().includes(q);
  }), [items, search, supFilter]);

  const openNew = () => { setEditing(null); setForm(empty); setDialog(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setDialog(true); };

  const save = async () => {
    if (!form.supplier_id || !form.internal_item) { toast.error('Supplier and internal item are required'); return; }
    setSaving(true);
    try {
      if (editing) await updateSupplierCode(editing.id, form);
      else await createSupplierCode(form);
      toast.success('Saved'); setDialog(false); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm('Delete this mapping?')) return;
    try { await deleteSupplierCode(it.id); reload(); }
    catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="supplier-codes-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><Barcode className="w-5 h-5" />Supplier Product Codes</h1>
          <p className="text-sm text-muted-foreground">Map a supplier's product code/name to your internal item. Keystone auto-suggests these on Inward Deliveries and Make Days.</p>
        </div>
        <Button onClick={openNew} data-testid="btn-new-code"><Plus className="w-4 h-4 mr-1" />New mapping</Button>
      </div>

      <Card><CardContent className="pt-4 flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search internal item, code…" className="pl-9" />
        </div>
        <Select value={supFilter} onValueChange={setSupFilter}>
          <SelectTrigger className="w-[260px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All suppliers</SelectItem>
            {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </CardContent></Card>

      {loading ? <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div> :
        <Card><CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr><th className="text-left px-4 py-3">Supplier</th><th className="text-left px-4 py-3">Internal item</th><th className="text-left px-4 py-3">Supplier code</th><th className="text-left px-4 py-3">Supplier product name</th><th className="text-left px-4 py-3">Unit</th><th className="text-right px-4 py-3">Actions</th></tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? <tr><td colSpan={6} className="text-center py-12 text-muted-foreground">No mappings yet.</td></tr> :
                filtered.map(it => (
                  <tr key={it.id} className="border-t hover:bg-zinc-50">
                    <td className="px-4 py-3">{supName(it.supplier_id)}</td>
                    <td className="px-4 py-3 font-medium">{it.internal_item}</td>
                    <td className="px-4 py-3 font-mono text-xs">{it.supplier_product_code || '—'}</td>
                    <td className="px-4 py-3">{it.supplier_product_name || '—'}</td>
                    <td className="px-4 py-3">{it.unit}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(it)}><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(it)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </CardContent></Card>}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit mapping' : 'New supplier product code'}</DialogTitle>
            <DialogDescription>Tell Keystone what a supplier calls something so we can auto-fill it on every receipt.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2"><Label>Supplier *</Label>
              <Select value={form.supplier_id} onValueChange={v => setForm({ ...form, supplier_id: v })}>
                <SelectTrigger><SelectValue placeholder="Choose a supplier" /></SelectTrigger>
                <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Internal item *</Label><Input value={form.internal_item} onChange={e => setForm({ ...form, internal_item: e.target.value })} placeholder="e.g. Cheese culture MA-4001" /></div>
            <div><Label>Supplier product code</Label><Input value={form.supplier_product_code} onChange={e => setForm({ ...form, supplier_product_code: e.target.value })} /></div>
            <div><Label>Unit</Label>
              <Select value={form.unit} onValueChange={v => setForm({ ...form, unit: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Supplier product name</Label><Input value={form.supplier_product_name} onChange={e => setForm({ ...form, supplier_product_name: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
